import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import { observeForResult } from 'cadenza/api-client/workorder-api';
import type { GenericGeocodingProgressEvent } from 'cadenza/geocoding/dto/geocoding-progress-event';
import type { EnrichmentResultEvent } from 'cadenza/geocoding/geocoding-progress-dialog/geocoding-progress-dialog';
import type { GeocodingType } from 'cadenza/geocoding/model/geocoding-type';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id';

export function observeWithGeocodingProgressDialog<T extends GeocodingType>(
  objecttypeId: ObjecttypeIdString,
  workOrderId: WorkOrderId,
  geocodingType: T,
  resultEventName: 'enrichment-object-type-created',
) {
  return import('./geocoding-progress-dialog').then(({ GeocodingProgressDialog }) => {
    const progressDialog = new GeocodingProgressDialog({ geocodingType, objecttypeId });

    let lockingExceptionThrown = false;
    const lockingExceptionListener = {
      'self-service-locking-exception': () => {
        lockingExceptionThrown = true;
      },
    };
    observeForResult<unknown, GenericGeocodingProgressEvent<T>, EnrichmentResultEvent>(
      workOrderId,
      resultEventName,
      lockingExceptionListener,
    )
      .progress((progressEvent) => {
        switch (progressEvent.step) {
          case 'self-service-step-geocoding':
          case 'self-service-step-geocoder-ip':
            progressDialog.onGeocodingUpdate(progressEvent);
            break;
          case 'self-service-step-geocoding-table-metadata':
          case 'self-service-step-geocoder-ip-table-metadata':
            progressDialog.onGeocodingStart(progressEvent);
            break;
        }
      })
      .then((result) => progressDialog.onGeocodingCompleted(result))
      .catch((error) => {
        progressDialog.onError(error);
        if (lockingExceptionThrown) {
          progressDialog.onLockingError();
        } else {
          progressDialog.onError(error);
        }
      });
    return progressDialog.connectAndShow();
  });
}
